import { CORE_SERVICES, TOOLS } from "@/lib/content";
import { PageHero } from "@/components/site/PageHero";
import { Eyebrow, Reveal } from "@/components/site/motion";
import { PillLink } from "@/components/site/Buttons";
import { ServiceFit } from "./services/ServiceFit";
import { Packages } from "./services/Packages";
import { CTABand, Process } from "@/components/site/Sections";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQ = [
  { q: "Which package is right for my business?", a: "Start with the decision or difficulty you need help with. Finance Foundations builds a monthly routine; Business Growth adds budgets, cash planning and grant readiness; Strategic Finance brings fractional CFO guidance. If the need is specific, a one-time project may be enough. We confirm the right scope together on a free consultation." },
  { q: "Can I start with a one-time project?", a: "Yes. Our Project / One-Time support covers system onboarding, bookkeeping clean-up, dashboard setup, grant submissions and financial modeling, with no ongoing commitment required." },
  { q: "Do you offer pricing in GBP?", a: "Yes. USD and GBP pricing is available. Book a call to discuss the right package and pricing for your business." },
  { q: "Which accounting tools do you work with?", a: "We work with platforms including QuickBooks, Zoho Books and Xero, alongside Google Workspace and AI assistants such as Gemini and Claude for reporting and strategy." },
  { q: "Do I need to have my books organised before getting in touch?", a: "No. If your records are behind or spread across systems, tell us where things stand. A catch-up or clean-up project can be the starting point before ongoing support." },
  { q: "Do you guarantee grant funding or investment?", a: "No. We support grant readiness, applications, financial models and reporting. Approval and investment decisions belong to the relevant funder or investor, and depend on their requirements." },
  { q: "Do I need to fit both the revenue and employee ranges?", a: "No. These annual revenue and employee ranges are a starting point, not eligibility limits or package prices. Your business may sit in different bands for revenue and team size. We will recommend support based on your complexity, priorities and stage. USD and GBP bands are separate guidance ranges, not live currency conversions." },
];

const CoreServices = () => (
  <section data-testid="core-services" className="bg-[#FAF8F5] py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal className="max-w-3xl">
        <Eyebrow>Our Core Financial Services</Eyebrow>
        <h2 data-testid="core-services-title" className="section-heading mt-6">From the books today<br /><em>to the decisions tomorrow.</em></h2>
      </Reveal>
      <div className="mt-14 grid gap-4 md:grid-cols-3">
        {CORE_SERVICES.map((s, i) => (
          <Reveal key={s.tag} delay={i * 0.1} testId={`core-service-${i + 1}`} className={`rounded-lg p-8 min-h-[22rem] flex flex-col ${i === 1 ? "bg-[#082F24] text-[#FAF8F5]" : i === 2 ? "bg-[#F4E5B0] text-[#082F24]" : "bg-[#DDECE3] text-[#082F24]"}`}>
            <span className={`font-mono-ega text-xs uppercase tracking-[0.2em] ${i === 1 ? "text-[#D4AF37]" : "text-[#0F5A47]"}`}>0{i + 1} · {s.tag}</span>
            <h3 className="mt-8 font-display text-3xl leading-tight">{s.title}</h3>
            <p className={`mt-4 text-base leading-relaxed ${i === 1 ? "text-[#E8F3EE]" : "text-[#294B3E]"}`}>{s.text}</p>
            <p data-testid={`core-service-fit-${s.id}`} className={`mt-6 pt-5 border-t text-sm leading-relaxed ${i === 1 ? "border-[#D4AF37]/50 text-[#E8F3EE]" : "border-[#0F5A47]/30 text-[#294B3E]"}`}>{s.fit}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ToolsGrid = () => (
  <section data-testid="tools-grid" className="bg-[#F3EFEA] py-24">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal><Eyebrow>Tools We Work With</Eyebrow><p className="mt-4 text-[#52635B] max-w-xl">We work with trusted platforms to streamline accounting, reporting, and AI-assisted strategy.</p></Reveal>
      <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {TOOLS.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.04} className="h-28 rounded-2xl bg-white border border-[#0F5A47]/10 grid place-items-center p-6">
            <img src={t.src} alt={t.name} className="max-h-12 max-w-full object-contain" />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Faq = () => (
  <section data-testid="services-faq" className="bg-[#FAF8F5] py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-10 lg:grid-cols-12">
      <Reveal className="lg:col-span-4"><Eyebrow>Questions</Eyebrow><h2 className="mt-6 font-display text-4xl sm:text-5xl tracking-tight text-[#082F24]">Good to know.</h2></Reveal>
      <Reveal delay={0.1} className="lg:col-span-7 lg:col-start-6">
        <Accordion data-testid="services-faq-accordion" type="single" collapsible className="space-y-3">
          {FAQ.map((f, i) => (
            <AccordionItem key={f.q} value={`f${i}`} className="service-faq-item rounded-lg border overflow-hidden">
              <AccordionTrigger data-testid={`faq-trigger-${i + 1}`} className="service-faq-trigger px-5 py-5 text-base font-semibold text-left leading-relaxed hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent data-testid={`faq-answer-${i + 1}`} className="px-5 pt-5 pb-6 text-[#294B3E] text-base leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

export default function Services() {
  return (
    <>
      <PageHero testId="services-hero" eyebrow="Services & Packages" lines={["The next step", "feels clearer", <em key="s" className="text-[#D4AF37]">with a partner.</em>]} intro="Behind on the books? Unsure what you can afford? Getting ready for a funding conversation? Find practical financial support built around your business, your stage and the decisions that matter now."><div className="mt-8 flex flex-wrap gap-3"><PillLink to="/services#packages" variant="gold" testId="services-packages-cta">Compare packages</PillLink><PillLink to="/services#find-your-fit" variant="ghostLight" testId="services-fit-cta">Find the right support</PillLink></div></PageHero>
      <Packages />
      <ServiceFit />
      <CoreServices />
      <Process />
      <ToolsGrid />
      <Faq />
      <CTABand />
    </>
  );
}
